'use client';

import { useState } from 'react';

import { ExternalLink } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { FinderGroup, MyGroup, MyInquiry } from '@/lib/types';
import { CampusIcon } from './CampusIcon';
import { StatusBadge, MineBadge } from './chips';
import { SheetHeader } from './SheetHeader';
import { GroupDetailsBody } from './GroupDetailsBody';
import { SeeJesusCorner, SeeJesusSheet } from './SeeJesus';
import { SJ_TITLE } from '@/lib/seeJesus';

interface GroupSheetProps {
  group: FinderGroup | null;
  /** Present when the signed-in visitor is in this group */
  mine?: MyGroup;
  /** Recent inquiries into this group by the visitor's household */
  inquiries?: MyInquiry[];
  /** An inquiry just went out from the sheet */
  onInquired?: () => void;
  /** Opens the "help me find a group" form */
  onHelp?: () => void;
  /** Cancelling an inquiry is handled outside the sheet */
  onCancel?: (inquiry: MyInquiry) => void;
  detailsUrl: string;
  inquiryFormUrl: string;
  /** For the inquiry form's "find another group" link */
  finderUrl: string;
  apiBaseUrl: string;
  onClose: () => void;
}

export function GroupSheet({
  group,
  mine,
  inquiries,
  onInquired,
  onHelp,
  onCancel,
  detailsUrl,
  inquiryFormUrl,
  finderUrl,
  apiBaseUrl,
  onClose,
}: GroupSheetProps) {
  const [explain, setExplain] = useState(false);
  const joinable = group ? !group.full && !group.paused : false;

  return (
    <>
      <SeeJesusSheet open={explain} onClose={() => setExplain(false)} />
      <ResponsiveSheet
        open={group !== null}
        onClose={onClose}
        maxWidth="max-w-6xl"
        noPanelPadding
        hideCloseButton={Boolean(group?.seeJesus)}
        panelClassName={
          group?.seeJesus
            ? 'sj-foil sj-frame-panel sj-type overflow-hidden rounded-3xl text-[#f6efe9]'
            : 'bg-white'
        }
        header={
          group ? (
            <SheetHeader
              icon={<CampusIcon congregationId={group.campusId} className="h-8 w-8" />}
              className={group.seeJesus ? 'sj-type' : ''}
              background={group.seeJesus ? null : undefined}
              titleClassName={group.seeJesus ? `${SJ_TITLE} pr-16 sm:pr-20` : ''}
              decoration={
                // Into the panel's corner, over the rings, clipped by the
                // rounded edge: the same spot as on the finder's cards.
                group.seeJesus ? (
                  <div className="absolute -top-1 -right-1 z-20">
                    <SeeJesusCorner />
                  </div>
                ) : null
              }
              eyebrow={
                mine || group.full || group.paused ? (
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    {mine && <MineBadge role={mine.role} />}
                    <StatusBadge full={group.full} paused={group.paused} />
                  </span>
                ) : undefined
              }
              title={group.title}
            />
          ) : null
        }
        footer={
          group ? (
            <div
              className={`flex items-center justify-center gap-4 px-6 py-3 text-xs sm:px-8 ${
                group.seeJesus
                  ? 'border-t border-white/10 text-[#f6efe9]/60'
                  : 'border-t border-gray-200 text-gray-500'
              }`}
            >
              <a
                href={`${detailsUrl}${group.id}`}
                className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline"
              >
                Open this group's page
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          ) : null
        }
      >
        <SheetPage name="main">
          {group && (
            <GroupDetailsBody
              compact
              apiBaseUrl={apiBaseUrl}
              group={group}
              mine={mine}
              inquiries={inquiries}
              onInquired={onInquired}
              onHelp={onHelp}
              onCancel={onCancel}
              finderUrl={finderUrl}
              inquiryFormUrl={inquiryFormUrl}
              onExplainSeeJesus={() => setExplain(true)}
            />
          )}
        </SheetPage>
      </ResponsiveSheet>
    </>
  );
}
